import * as React from "react";
import { useParams } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@src/ui/views/card";
import { Calendar } from "lucide-react";
import { Button } from "@src/ui/views/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@src/ui/views/table";
import { Avatar, AvatarImage, AvatarFallback } from "@src/ui/views/avatar";
import { ResponsiveContainer, BarChart, XAxis, YAxis, Tooltip, Bar } from "recharts";
import { useCancelWelfareInstanceMutation, useGetAllWelfareInstancesQuery } from "@src/features/welfare/redux/welfare-api";
import type { WelfareInstance } from "@src/features/welfare/welfare-models";
import { Input } from "@src/ui/views/input";

import { useNavigate } from "react-router-dom";
import { WelfareTypeContext } from "./welfare-layout";


export function WelfareInstancesScreen() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const today = new Date().toISOString().split("T")[0];
  const navigate = useNavigate();
  const { instanceType } = React.useContext(WelfareTypeContext);

  // 2) date range
  const [startDate, setStartDate] = React.useState(today);
  const [endDate, setEndDate] = React.useState("");

  // 3) fetch instances
  const { data, isLoading, isError, refetch } = useGetAllWelfareInstancesQuery(
    { spaceId: spaceId!, instanceType, startDate, endDate },
    { skip: !spaceId }
  );
  const [selectedInstance, setSelectedInstance] = React.useState<WelfareInstance | null>(null);
  const [cancelInstance, { isLoading: isCancelling }] =
    useCancelWelfareInstanceMutation();

  if (isLoading) return <p>Loading instances…</p>;
  if (isError)   return <p>Error loading instances</p>;

  const groups = (data?.data ?? []) as { title: string; data: WelfareInstance[] }[];
  const allInstances = groups.flatMap(g => g.data);
  const totalBookings = allInstances.reduce((sum, inst) => sum + inst.participatingUsers.length, 0);
  const chartData = groups.map(g => ({
    date: g.title,
    bookings: g.data.reduce((s, inst) => s + inst.participatingUsers.length, 0),
  }));

  return (
    <div className="space-y-8 p-6 bg-white rounded-lg shadow">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => navigate("definitions")}
      >
        View Definitions
      </Button>

      {/* Top cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Total Bookings</CardTitle>
            <Calendar className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent className="text-3xl font-bold">{totalBookings}</CardContent>
        </Card>
        {/* more cards… */}
      </div>

      {/* Date range picker */}
      <div className="flex items-end space-x-4">
        <label className="flex flex-col">
          <span className="text-sm font-medium">Start Date</span>
          <Input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="mt-1"
          />
        </label>
        <label className="flex flex-col">
          <span className="text-sm font-medium">End Date</span>
          <Input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="mt-1"
          />
        </label>
        <Button onClick={() => refetch()}>Apply</Button>
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer>
          <BarChart data={chartData}>
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="bookings" fill="#4f46e5" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Grouped instances table */}
      {groups.map(group => (
        <section key={group.title} className="space-y-2">
          <h3 className="text-xl font-medium">{group.title}</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Description</TableHead>
                <TableHead># Participants</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {group.data.map(inst => (
                <TableRow
                  key={inst.instanceId}
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => setSelectedInstance(inst)}
                >
                  <TableCell>{inst.instanceName}</TableCell>
                  <TableCell>
                    {String(inst.startHour).padStart(2, "0")}:
                    {String(inst.startMinute).padStart(2, "0")}
                  </TableCell>
                  <TableCell>{inst.descript}</TableCell>
                  <TableCell>
                    <div className="flex -space-x-2">
                      {inst.participatingUsers.map(u => (
                        <Avatar
                          key={u.userId}
                          className="h-6 w-6 rounded-full border-2 border-white"
                        >
                          <AvatarImage src={u.profilePic || ""} alt={u.userId} />
                          <AvatarFallback>
                            {u.userId.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      ))}

      {/* Participants Modal */}
      {selectedInstance && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-1/3">
            <h4 className="text-xl font-semibold mb-4">
              Participants for {selectedInstance.instanceName}
            </h4>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {selectedInstance.participatingUsers.map(u => (
                <div key={u.userId} className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={u.profilePic || ""} alt={u.userId} />
                    <AvatarFallback>
                      {u.userId.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-medium">{u.userId}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <Button
                variant="destructive"
                disabled={isCancelling}
                onClick={async () => {
                  await cancelInstance({
                    spaceId: spaceId!,
                    instanceId: selectedInstance.instanceId,
                  }).unwrap();
                  setSelectedInstance(null);
                }}
              >
                {isCancelling ? "Cancelling…" : "Cancel Instance"}
              </Button>
              <Button onClick={() => setSelectedInstance(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
